
import React from 'react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Check, X, User } from 'lucide-react';

const BackgroundScanning = () => {
  // Create a grid of background items
  const items = Array.from({ length: 40 }).map((_, i) => ({
    id: i,
    // Randomize initial states and timing
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 2,
    isMatch: Math.random() > 0.8, // 20% match rate
    x: Math.random() * 100,
    y: Math.random() * 100,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.08]">
      {items.map((item) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ 
            opacity: [0, 1, 1, 0],
            scale: [0.5, 1, 1, 0.5],
          }}
          transition={{ 
            duration: item.duration, 
            repeat: Infinity, 
            delay: item.delay,
            ease: "easeInOut"
          }}
          style={{ 
            position: 'absolute', 
            left: `${item.x}%`, 
            top: `${item.y}%`,
          }}
          className="flex flex-col items-center gap-1"
        >
          <div className="w-8 h-8 rounded-full bg-brand-darkgrey/10 flex items-center justify-center border border-brand-darkgrey/5">
            <User className="w-4 h-4 text-brand-darkgrey" />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0, 0, 1, 1, 0],
              scale: [0, 0, 1.2, 1, 0]
            }}
            transition={{ 
              duration: item.duration, 
              repeat: Infinity, 
              delay: item.delay,
              times: [0, 0.4, 0.5, 0.8, 1]
            }}
            className={`flex items-center justify-center w-5 h-5 rounded-full ${item.isMatch ? 'bg-emerald-500' : 'bg-red-400'}`}
          >
            {item.isMatch ? (
              <Check className="w-3 h-3 text-white stroke-[3px]" />
            ) : (
              <X className="w-3 h-3 text-white stroke-[3px]" />
            )}
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
};

const Hero = () => {
  return (
    <section className="relative pt-48 pb-20 md:pt-72 md:pb-32 overflow-hidden bg-white">
      {/* Enhanced Background layer */}
      <div className="absolute inset-0 -z-10">
        {/* Subtle grid pattern - More visible */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808022_1px,transparent_1px),linear-gradient(to_bottom,#80808022_1px,transparent_1px)] bg-[size:40px_40px]" />
        
        {/* Color Blobs - Significantly more opaque */}
        <div className="absolute top-0 left-0 right-0 h-full bg-[radial-gradient(circle_at_50%_0%,rgba(88,81,211,0.25),transparent_75%)]" />
        <div className="absolute -top-[15%] -left-[10%] w-[60%] h-[60%] bg-brand-blue/30 rounded-full blur-[100px]" />
        <div className="absolute top-[10%] -right-[15%] w-[50%] h-[70%] bg-brand-yellow/30 rounded-full blur-[90px]" />
        
        {/* Additional bottom glow for contrast */}
        <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-t from-white to-transparent" />
      </div>
      
      {/* Dynamic background scanning animation */}
      <BackgroundScanning />
      
      <div className="container-custom relative mt-8 md:mt-12">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-extrabold tracking-tight text-brand-darkgrey leading-[1.1] mb-4">
            Increase your hiring speed. <span className="text-brand-blue">Not your database spend.</span>
          </h1>
          
          <h2 className="text-xl md:text-2xl text-brand-grey mb-12 max-w-3xl mx-auto leading-relaxed font-medium mt-10">
            Cutshort surfaces the most relevant candidates first — helping teams hire faster without extra portal spend.
          </h2>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-24">
            <Button 
              asChild
              size="lg" 
              className="h-16 px-10 text-xl font-bold bg-brand-blue hover:bg-brand-blue/90 rounded-2xl shadow-lg shadow-brand-blue/20"
            >
              <a href="https://share.hsforms.com/1y0l7U0OHRo2JLsKVJLBhnAbpcum" target="_blank" rel="noopener noreferrer">
                Request a demo
              </a>
            </Button>
          </div>
          
          <div className="pt-8 flex flex-col items-center">
            <div className="flex flex-col items-center gap-1 group cursor-default">
              <p className="text-sm font-semibold text-brand-grey opacity-60">
                Powered by
              </p>
              <div className="flex flex-col items-center">
                <span className="text-xl font-bold text-brand-darkgrey tracking-tight">
                  Cutshort Talent Intelligence Engine
                </span>
                <div className="h-[3px] w-full bg-brand-yellow mt-1 rounded-full scale-x-100 group-hover:scale-x-110 transition-transform duration-300" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
